import {
  PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from "react";
import { FilterChipType } from "../../../../components/search-filter/types";
import {
  RebatesFilterAction,
  RebatesFilterContextType,
  RebateGoalStatus,
  RebatesFilterStateType,
} from "../../types/types";

export const sectionIdConstants = {
  agreementNumber: "SELECTEDAGREEMENTNUMBER",
  seller: "SELECTEDSELLER",
  customer: "SELECTEDCUSTOMER",
  terms: "SELECTEDTERMS",
  salesRegions: "SELECTEDSALESREGIONS",
  comletionLevels: "SELECTEDCOMPLETIONLEVELS",
  validity: "SELECTEDVALIDITY",
  agreementName: "SELECTEDAGREEMENTNAME",
  year: "SELECTEDYEAR",
  goalStatus: "SELECTEDGOALSTATUS",
};

export const defaultHandleRebatesGoalStatusCodes: RebateGoalStatus[] = [
  "ACTIVE",
  "ONGOING",
  "REACHED",
];

export const goalStatusOptions: { value: RebateGoalStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "FAILED", label: "Failed" },
  { value: "NEGOTIATED", label: "Negotiated" },
  { value: "ONGOING", label: "Ongoing" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "REACHED", label: "Reached" },
  { value: "VOID", label: "Void" },
];

export const RebatesFilterContext =
  createContext<RebatesFilterContextType | null>(null);

const initialFilterState: RebatesFilterStateType = {
  selectedAgreementNumber: "",
  selectedSalesRegions: [],
  selectedCustomer: null,
  selectedSeller: undefined,
  selectedTerms: [],
  selectedYear: undefined,
  selectedValidity: undefined,
  selectedGoalStatusCodes: [],
};

const createInitialFilterState = (
  selectedGoalStatusCodes: RebateGoalStatus[]
): RebatesFilterStateType => ({
  ...initialFilterState,
  selectedGoalStatusCodes,
});

const noGoalStatusCodes: RebateGoalStatus[] = [];

const filterReducer = (
  state: RebatesFilterStateType,
  action: RebatesFilterAction
): RebatesFilterStateType => {
  switch (action.type) {
    case "SET_AGREEMENT_NUMBER":
      return { ...state, selectedAgreementNumber: action.payload };
    case "SET_CUSTOMER":
      return { ...state, selectedCustomer: action.payload };
    case "SET_SELLER":
      return { ...state, selectedSeller: action.payload };
    case "SET_YEAR":
      return { ...state, selectedYear: action.payload };
    case "SET_VALIDITY":
      return { ...state, selectedValidity: action.payload };
    case "TOGGLE_GOAL_STATUS": {
      const exists = state.selectedGoalStatusCodes.includes(action.payload);
      return {
        ...state,
        selectedGoalStatusCodes: exists
          ? state.selectedGoalStatusCodes.filter(
              (status) => status !== action.payload
            )
          : [...state.selectedGoalStatusCodes, action.payload],
      };
    }
    case "TOGGLE_SALES_REGION": {
      const exists = state.selectedSalesRegions.some(
        (r) => r.id === action.payload.id
      );
      return {
        ...state,
        selectedSalesRegions: exists
          ? state.selectedSalesRegions.filter((r) => r.id !== action.payload.id)
          : [...state.selectedSalesRegions, action.payload],
      };
    }
    case "TOGGLE_TERM": {
      const exists = state.selectedTerms.some(
        (t) => t.id === action.payload.id
      );
      return {
        ...state,
        selectedTerms: exists
          ? state.selectedTerms.filter((t) => t.id !== action.payload.id)
          : [...state.selectedTerms, action.payload],
      };
    }
    case "CLEAR":
      return createInitialFilterState(action.payload);
  }
};

export const getChipsFromFilterState = (
  filterState: RebatesFilterStateType
) => {
  const {
    selectedAgreementNumber,
    selectedCustomer,
    selectedSalesRegions,
    selectedSeller,
    selectedTerms,
    selectedYear,
    selectedValidity,
    selectedGoalStatusCodes,
  } = filterState;
  const chips: FilterChipType[] = [];

  if (selectedAgreementNumber) {
    const sectionId = sectionIdConstants.agreementNumber;
    chips.push({ label: `No. ${selectedAgreementNumber}`, sectionId });
  }

  if (selectedCustomer) {
    const sectionId = sectionIdConstants.customer;
    chips.push({ label: selectedCustomer.label, sectionId });
  }

  if (selectedSeller) {
    const sectionId = sectionIdConstants.seller;
    chips.push({ label: selectedSeller.label, sectionId });
  }

  if (selectedSalesRegions.length > 0) {
    const sectionId = sectionIdConstants.salesRegions;
    chips.push(
      ...selectedSalesRegions.map((salesRegion) => ({
        label: salesRegion.name,
        sectionId,
      }))
    );
  }

  if (selectedTerms.length > 0) {
    const sectionId = sectionIdConstants.terms;
    chips.push(
      ...selectedTerms.map((term) => ({
        label: term.label,
        sectionId,
      }))
    );
  }

  if (selectedYear) {
    const sectionId = sectionIdConstants.year;
    chips.push({ label: `${selectedYear.value}`, sectionId });
  }

  if (selectedValidity) {
    const sectionId = sectionIdConstants.validity;
    if (selectedValidity.startDate && selectedValidity.endDate) {
      chips.push({
        label: [
          selectedValidity.startDate.toLocaleDateString(),
          selectedValidity.endDate.toLocaleDateString(),
        ].join(" - "),
        sectionId,
      });
    }
  }

  if (selectedGoalStatusCodes.length > 0) {
    const sectionId = sectionIdConstants.goalStatus;
    chips.push(
      ...selectedGoalStatusCodes.map((status) => ({
        label:
          goalStatusOptions.find((option) => option.value === status)?.label ??
          status,
        sectionId,
      }))
    );
  }

  return chips;
};

const RebatesFilterProvider = ({
  children,
  initialGoalStatusCodes = noGoalStatusCodes,
}: PropsWithChildren<{ initialGoalStatusCodes?: RebateGoalStatus[] }>) => {
  const [filterState, dispatch] = useReducer(
    filterReducer,
    initialGoalStatusCodes,
    createInitialFilterState
  );
  const [selectedFilterChips, setSelectedFilterChips] = useState<
    FilterChipType[]
  >(() => getChipsFromFilterState(filterState));
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);

  const clearFilter = useCallback(() => {
    dispatch({ type: "CLEAR", payload: initialGoalStatusCodes });
    setSelectedFilterChips(
      getChipsFromFilterState(createInitialFilterState(initialGoalStatusCodes))
    );
    setOpenSectionIds([]);
  }, [initialGoalStatusCodes]);

  const toggleSectionId = useCallback(
    (sectionId: string) =>
      setOpenSectionIds((previousState) =>
        previousState.includes(sectionId)
          ? previousState.filter((item) => item !== sectionId)
          : [...previousState, sectionId]
      ),
    []
  );

  const contextValue = useMemo(
    () => ({
      filterState,
      dispatch,
      filterIsOpen,
      setFilterIsOpen,
      clearFilter,
      selectedFilterChips,
      setSelectedFilterChips,
      openSectionIds,
      toggleSectionId,
      setOpenSectionIds,
    }),
    [
      filterState,
      filterIsOpen,
      clearFilter,
      selectedFilterChips,
      openSectionIds,
      toggleSectionId,
    ]
  );

  return (
    <RebatesFilterContext.Provider value={contextValue}>
      {children}
    </RebatesFilterContext.Provider>
  );
};

export { RebatesFilterProvider };
